import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { ColorFilter } from "~/components/Filters/ColorFilter/ColorFilter";
import { ColorRangeControl } from "~/components/Filters/ColorFilter/components/ColorRangeControl";
import { CustomColorPickerControl } from "~/components/Filters/ColorFilter/components/CustomColorPickerControl";
import { DefaultColorFilterControl } from "~/components/Filters/ColorFilter/components/DefaultColorPickerControl";
import { FilterCard } from "~/components/Filters/FilterCard";
import { useTemporaryFeatureFlags } from "~/hooks/useTemporaryFeatureFlags";
import { addColorFilterValueAction, addColorRangeFilterValueAction, clearColorFilterValuesAction, removeColorFilterValueAction, removeFilterAction } from "~/store/filters/actions";
import { selectedColorsFiltersSelector } from "~/store/filters/selectors";
export const ColorFilterCard = /*#__PURE__*/ memo((param)=>{
    let { colors, ...props } = param;
    const dispatch = useDispatch();
    const selectedColors = useSelector(selectedColorsFiltersSelector);
    const customColorPickerRef = useRef(null);
    const { colorFilter: hasColorFilterFeatureFlag } = useTemporaryFeatureFlags();
    const clearValues = useCallback(()=>{
        dispatch(clearColorFilterValuesAction());
        if (customColorPickerRef.current) {
            customColorPickerRef.current.clearSavedHexes();
        }
    }, [
        dispatch
    ]);
    const handleColorToggle = useCallback((param)=>{
        let { color, isSelected } = param;
        if (isSelected) {
            dispatch(addColorFilterValueAction({
                color: color.hex
            }));
        } else {
            dispatch(removeColorFilterValueAction({
                color: color.hex
            }));
        }
    }, [
        dispatch
    ]);
    const [onColorRangeChange] = useDebouncedCallback((value)=>{
        dispatch(addColorRangeFilterValueAction(value));
    }, 300);
    const onRemoveFilter = useCallback((filter)=>{
        dispatch(removeFilterAction({
            filter
        }));
        clearValues();
    }, [
        dispatch,
        clearValues
    ]);
    const onRemoveHex = useCallback((hex)=>{
        dispatch(removeColorFilterValueAction({
            color: hex
        }));
    }, [
        dispatch
    ]);
    return /*#__PURE__*/ _jsx(FilterCard, {
        currentFilter: "color",
        onClearClick: clearValues,
        onRemoveFilter: onRemoveFilter,
        ...props,
        children: /*#__PURE__*/ _jsx("div", {
            className: "flex flex-col gap-4",
            children: hasColorFilterFeatureFlag ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(DefaultColorFilterControl, {
                        colors: colors,
                        onClick: handleColorToggle,
                        selectedColors: selectedColors
                    }),
                    /*#__PURE__*/ _jsx(CustomColorPickerControl, {
                        ref: customColorPickerRef,
                        onColorSelect: handleColorToggle,
                        onRemoveHex: onRemoveHex,
                        selectedColors: selectedColors
                    }),
                    /*#__PURE__*/ _jsx(ColorRangeControl, {
                        onValueChange: onColorRangeChange
                    })
                ]
            }) : /*#__PURE__*/ _jsx(ColorFilter, {
                colors: colors,
                onClick: handleColorToggle,
                selectedColors: selectedColors
            })
        })
    });
});
ColorFilterCard.displayName = "ColorFilterCard";
